import { JSONParser } from '@streamparser/json'
import { getActiveSession } from '@attensi/auth'
import { getFlowiseRequest } from '@/api/api.flowise'
import { computed, ref, Ref } from 'vue'
import { PUser } from '@attensi/portal-models'

export type ChatMessage = {
  messageType: 'Question' | 'Response' | 'Error'
  text: string
}

export type ChatConfig = {
  flowId: string
  title: string
  questionSuggestions: string[]
}

export type FlowiseResponseFragment = {
  fragmentType: 'Start' | 'Token' | 'End' | 'Noop' | 'Response'
  text: string
}

// Link to documentation: https://www.npmjs.com/package/@streamparser/json#user-content-stream-parsing-a-fetch-request-returning-a-json-array
// We use this specifically for parsing a streamed array of JSON objects coming in from the /api/Flowise/query end-point
const jsonparser = new JSONParser({
  stringBufferSize: undefined,
  paths: ['$.*'],
  separator: '',
})

// This hook is intended to be used for asking questions to our own AI API
// The answer will be returned as a Stream of an array containing JSON objects
// The JSON objects represent FlowiseResponseFragments, that each try to describe their meaning in the context of an ongoing stream.
// The API is actively worked on at this moment, and we lack fx. a clear agreement on if fx. the back-end should wrap and generalize errors for the clients,
// or if we are to handle it on our own.
// Link to Swagger documentation for AI-API: https://ai-api.attensi.com/swagger/index.html
export const useFlowiseQuestionStream = (
  flowId: string,
  user: Ref<PUser | undefined>
) => {
  const isLoading = ref(false)
  const userId = computed(() => user.value?.id.toString() || '0')

  const askQuestion = async (
    question: string,
    onChunk: (chunk: FlowiseResponseFragment) => void,
    onError: (errorMessage: string) => void
  ) => {
    // This function is called by jsonparser.write(), and is the handler for the JSON stream parser
    jsonparser.onValue = ({ value }) => {
      const flowiseResponseFragment = value as FlowiseResponseFragment
      onChunk(flowiseResponseFragment)
    }

    try {
      isLoading.value = true
      const { accessToken } = await getActiveSession()
      const request = getFlowiseRequest(
        accessToken,
        question,
        flowId,
        userId.value
      )
      const response = await fetch(request)
      if (!response.body) return
      if (!response.ok) {
        throw new Error(`Status ${response.status}: ${response.statusText}`)
      }

      const reader = response.body.getReader()
      // Infinite loop
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break

        // Will tokenize, parse and call onValue(), with the streamed chunk
        if (value) {
          jsonparser.write(value)
        }
      }
    } catch (error) {
      console.log('Error on POST /api/Flowise/query: ', error)
      onError('Something went wrong. Please try again') // TODO: figure out a proper way to handle errors between Flowise and us
    } finally {
      isLoading.value = false
    }
  }

  return { askQuestion, isLoading }
}
